import React, { createContext, useContext, useCallback, useState } from "react";
import { useApi } from "./useApi";
import { useFlow } from "./useFlow";
import { useBiblicalMetrics } from "./useBiblicalMetrics";
import { useAuth } from "../contexts/AuthContext";
import storageMonitor from '../utils/storageMonitor.js';

// Performance optimization configuration (obfuscated names)
const PERF_MODE = process.env.REACT_APP_PERF_MODE || "V1"; // Default to V1 (current behavior)
const PERF_BUFFER = parseInt(process.env.REACT_APP_PERF_BUFFER) || 5;

// Only log in development
if (process.env.NODE_ENV === "development") {
  // console.log(
  //   "🔧 Performance mode:",
  //   PERF_MODE === "V2" ? "Optimized" : "Standard"
  // );

  // Add enhanced storage checker to global scope using new storage monitor
  window.__checkStorage = async () => {
    try {
      console.group("🚀 Enhanced Storage Analysis");

      // Get comprehensive storage stats
      const stats = await storageMonitor.getStorageStats();
      const breakdown = await storageMonitor.getStorageBreakdown();
      const migrationInfo = await storageMonitor.shouldMigrateToIndexedDB();

      // Display current storage info
      console.group("📊 Current Storage Status");
      // console.log(`🏗️  Backend: ${stats.backend}`);
      // console.log(`📦 Total Usage: ${stats.formattedUsage}`);
      if (stats.formattedQuota && stats.quota > 0) {
        // console.log(`📏 Quota: ${stats.formattedQuota}`);
        // console.log(`📈 Usage: ${stats.usagePercentage.toFixed(1)}%`);
        // console.log(`💚 Available: ${stats.formattedAvailable}`);
      }
      // console.log(`🔹 Status: ${stats.status} - ${stats.statusMessage}`);
      console.groupEnd();

      // Display data breakdown
      console.group("📋 Data Breakdown");
      Object.entries(breakdown).forEach(([type, data]) => {
        if (type !== 'total') {
          // console.log(`${type}: ${data.formattedSize} (${data.count} items)`);
        }
      });
      // console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      // console.log(`🎯 Total: ${breakdown.total.formattedSize} (${breakdown.total.count} items)`);
      console.groupEnd();

      // Migration recommendations
      console.group("💡 Migration Recommendations");
      // console.log(`📈 Should migrate to IndexedDB: ${migrationInfo.recommended ? 'YES' : 'NO'}`);
      // console.log(`🧠 Reason: ${migrationInfo.reason}`);
      if (migrationInfo.benefits) {
        // console.log("✅ Benefits of IndexedDB:");
        // migrationInfo.benefits.forEach(benefit => console.log(`   • ${benefit}`));
      }
      console.groupEnd();

      console.groupEnd();

      return {
        stats,
        breakdown,
        migration: migrationInfo,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error("❌ Storage analysis failed:", error);
      return { error: error.message };
    }
  };

  // Add storage cleanup utility
  window.__cleanupStorage = async (options = {}) => {
    try {
      // console.log("🧹 Starting storage cleanup...");
      const result = await storageMonitor.performCleanup(options);
      // console.log("✅ Cleanup completed:", result);
      return result;
    } catch (error) {
      console.error("❌ Cleanup failed:", error);
      return { error: error.message };
    }
  };

  // Add migration reset utility for testing
  window.__resetMigration = () => {
    try {
      localStorage.removeItem('storage_migration_completed');
      // console.log("🔄 Migration flag reset. Refresh the page to trigger migration again.");
      return { success: true };
    } catch (error) {
      console.error("❌ Reset failed:", error);
      return { error: error.message };
    }
  };

  // console.log("💡 Run __checkStorage(), __cleanupStorage(), or __resetMigration() in console for storage management");
}

// Create Flow Context
const FlowContext = createContext();

// Flow Provider component
export const FlowProvider = ({ children }) => {
  const {
    askQuestion,
    getSessionSummary,
    isLoading,
    shouldTriggerSummary,
    error,
    clearError,
    rateLimitError,
    clearRateLimitError,
    sessionId,
    interactionCount,
    queryAPI,
    systemStyle,
  } = useApi();

  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onViewportChange,
    edgeType,
    setEdgeType,
    addResponseNode,
    addHighlightedTextNode,
    updateNodeData,
    toggleInputNode,
    nodeCount,
    focusNode,
    setReactFlowInstance,
    collapseAllNodes,
    expandAllNodes,
    getExpandedNodesCount,
    updateNodeExpansion,
    bringNodeToFront,
    // Workflow management
    currentWorkflowName,
    setCurrentWorkflowName,
    currentFirestoreSessionId,
    setCurrentFirestoreSessionId,
    hasUnsavedChanges,
    isLoadingWorkflow,
    saveState,
    loadWorkflow,
    resetWorkflow,
    nodeIdCounter,
    clearAutosave,
    transferWorkflowOwnership,
    // Recovery management
    showRecoveryPrompt,
    recoveryData,
    recoverAutosave,
    dismissRecovery,
    lastAutosaveTime,
    // Exploration management
    startFreshExploration,
  } = useFlow();

  const { metrics: biblicalMetrics, updateMetrics } = useBiblicalMetrics();
  const { user } = useAuth();

  // Subscription error state - for showing PaywallModal on permission-denied errors
  const [subscriptionError, setSubscriptionError] = useState(null);

  // Helper to detect subscription errors
  const handleSubscriptionError = useCallback((error) => {
    // Check if error message mentions subscription (primary check)
    const isSubscriptionRelated = error.message &&
      (error.message.toLowerCase().includes('subscription') ||
       error.message.toLowerCase().includes('subscribe'));

    // Check if it's a permission-denied error (any format)
    const isPermissionDenied =
      error.code === 'permission-denied' ||
      error.code === 'functions/permission-denied' ||
      (error.message && error.message.toLowerCase().includes('permission'));

    // If message mentions subscription OR it's a permission error with subscription message
    if (isSubscriptionRelated) {
      console.log('🔒 Subscription error detected:', {
        code: error.code,
        message: error.message,
        errorType: error.constructor?.name
      });

      setSubscriptionError({
        code: error.code,
        message: error.message,
        timestamp: Date.now()
      });
      return true; // Is subscription error
    }

    return false; // Not a subscription error
  }, []);

  // Clear subscription error
  const clearSubscriptionError = useCallback(() => {
    setSubscriptionError(null);
  }, []);

  // New helper function - looks like performance optimization
  const getOptimizedData = useCallback(
    (nodeId) => {
      // Only runs in V2 mode
      if (PERF_MODE !== "V2") {
        return null; // Return null for V1 mode
      }

      const buffer = [];
      let currentId = nodeId;

      // Walk up the parent chain to get ancestor context
      for (let depth = 0; depth < PERF_BUFFER && currentId; depth++) {
        const node = nodes.find((n) => n.id === currentId);
        if (!node) break;

        // Add to beginning (oldest first)
        buffer.unshift({
          question: node.data.question,
          response: node.data.response,
        });

        // Find parent edge
        const parentEdge = edges.find((e) => e.target === currentId);
        if (!parentEdge) break;

        currentId = parentEdge.source;
      }
      return buffer;
    },
    [nodes, edges]
  );

  // Handle main question submission
  const handleQuestionSubmit = useCallback(
    async (question) => {
      try {
        clearError();
        // Force new session for every landing page question
        const userProfile = user ? { personality: user.personality, name: user.name } : null;
        const response = await askQuestion(question, [], true, userProfile);

        // console.log("ASK QUESTION START:");
        // console.log(response);
        // console.log("ASK QUESTION END:");
        // The response structure is: { success: true, response: { response: "content", actions: [...], question: "..." } }
        const apiResponseData = response.response || {};

        const responseData = {
          response:
            apiResponseData.response ||
            "I'd be happy to help you learn about the Bible!",
          actions: apiResponseData.actions || [],
          question: question,
          timestamp: Date.now(),
          id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          sessionId: response.sessionId,
          interactionId: response.interactionId,
          currentlyExploring: apiResponseData.currentlyExploring,
          booksMentioned: apiResponseData.booksMentioned || [],
          versesReferenced: apiResponseData.versesReferenced || [],
        };

        const nodeId = addResponseNode(responseData);

        // Update biblical metrics if we have a topic
        if (apiResponseData.currentlyExploring) {
          updateMetrics({ ...responseData, nodeId }, null);
        }

        // Hide input node after first question and auto-zoom to full view
        if (nodeCount === 0) {
          toggleInputNode(false);

          // Auto-zoom to MAXIMUM and center when starting a new topic from landing page
          // Increase delay to ensure ReactFlow instance is ready and node is rendered
          setTimeout(() => {
            if (focusNode) {
              // Use maximum zoom for first node (1.5 is the max zoom level)
              focusNode(nodeId, null, 1000, 1.5); // Pass max zoom level
              // console.log("Centering on first node with max zoom:", nodeId);
            }
          }, 1000); // Increased delay to ensure everything is ready
        }

        // Summary is now handled silently in the backend as context for future API calls

        return nodeId;
      } catch (error) {
        console.error("Failed to submit question:", error);
        // Check if it's a subscription error
        handleSubscriptionError(error);
        throw error;
      }
    },
    [
      askQuestion,
      addResponseNode,
      nodeCount,
      toggleInputNode,
      getSessionSummary,
      clearError,
      updateMetrics,
      handleSubscriptionError,
    ]
  );

  // Helper function to find node ID when sourceNodeData.nodeId is undefined
  const findNodeIdByData = useCallback((sourceNodeData) => {
    if (sourceNodeData.nodeId) {
      return sourceNodeData.nodeId;
    }

    // Find node by matching question and response data
    const matchingNode = nodes.find(node =>
      node.data.question === sourceNodeData.question &&
      node.data.response === sourceNodeData.response
    );

    if (matchingNode) {
      console.log(`🔍 Found matching node: ${matchingNode.id} for question: "${sourceNodeData.question}"`);
      return matchingNode.id;
    }

    console.warn("❌ Could not find matching node for sourceNodeData:", sourceNodeData);
    return undefined;
  }, [nodes]);

  // Handle action button clicks
  const handleActionClick = useCallback(
    async (action, sourceNodeData) => {
      try {
        clearError();
        const contextQuestion = `${action} (Context: ${sourceNodeData.question})`;
        const response = await askQuestion(contextQuestion, [
          {
            question: sourceNodeData.question,
            response: sourceNodeData.response,
          },
        ], false, user ? { personality: user.personality, name: user.name } : null);

        // The response structure is: { success: true, response: { response: "content", actions: [...], question: "..." } }
        const apiResponseData = response.response || {};

        const responseData = {
          response:
            apiResponseData.response ||
            "I'd be happy to help you learn about the Bible!",
          actions: apiResponseData.actions || [],
          question: contextQuestion,
          timestamp: Date.now(),
          id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          sessionId: response.sessionId,
          interactionId: response.interactionId,
          currentlyExploring: apiResponseData.currentlyExploring,
          booksMentioned: apiResponseData.booksMentioned || [],
          versesReferenced: apiResponseData.versesReferenced || [],
        };

        const resolvedNodeId = findNodeIdByData(sourceNodeData);
        const nodeId = addResponseNode(responseData, resolvedNodeId);

        // Update biblical metrics if we have a topic
        if (apiResponseData.currentlyExploring) {
          updateMetrics({ ...responseData, nodeId }, resolvedNodeId);
        }

        // Summary is now handled silently in the backend as context for future API calls

        return nodeId;
      } catch (error) {
        console.error("Failed to handle action:", error);
        // Check if it's a subscription error
        handleSubscriptionError(error);
        throw error;
      }
    },
    [askQuestion, addResponseNode, getSessionSummary, clearError, updateMetrics, findNodeIdByData, handleSubscriptionError]
  );

  // Handle text highlighting
  const handleTextHighlight = useCallback(
    async (selectedText, sourceNodeData) => {
      try {
        clearError();

        // Check if selectedText is a custom question object or regular string
        let response;
        let displayQuestion;
        let actualSelectedText;

        // Build context array based on mode
        let contextArray;
        if (PERF_MODE === "V2") {
          // Optimized mode: use ancestor path only
          contextArray = getOptimizedData(sourceNodeData.nodeId);
        } else {
          // V1 mode: current behavior
          contextArray = [
            {
              question: sourceNodeData.question,
              response: sourceNodeData.response,
              highlightedText: typeof selectedText === "object" ? selectedText.context || "" : selectedText,
            },
          ];
        }

        if (typeof selectedText === "object" && selectedText.isCustomQuestion) {
          // Custom question from Chat/Ask button - KEEP AS-IS (complex case with context)
          const contextQuestion = selectedText.context
            ? `${selectedText.question} (Context: "${selectedText.context}")`
            : selectedText.question;
          displayQuestion = selectedText.question; // Clean question for header
          actualSelectedText = selectedText.context || "";

          response = await askQuestion(contextQuestion, contextArray);
        } else {
          // Regular "Learn More" selection - MIGRATE TO V2
          actualSelectedText = selectedText;
          displayQuestion = `Learn more about: "${selectedText}"`;

          if (systemStyle === "2") {
            // V2: Use clean action code, backend handles prompt
            response = await queryAPI("learn_more", selectedText, contextArray, false, user ? { personality: user.personality, name: user.name } : null);
          } else {
            // V1: Keep old behavior with frontend prompt
            const contextQuestion = `Please explain more about: "${selectedText}"`;
            response = await askQuestion(contextQuestion, contextArray, false, user ? { personality: user.personality, name: user.name } : null);
          }
        }

        // The response structure is: { success: true, response: { response: "content", actions: [...], question: "..." } }
        const apiResponseData = response.response || {};

        const responseData = {
          response:
            apiResponseData.response ||
            "I'd be happy to help you learn about the Bible!",
          actions: apiResponseData.actions || [],
          question: displayQuestion, // Use the clean question for the header
          timestamp: Date.now(),
          id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          sessionId: response.sessionId,
          interactionId: response.interactionId,
          currentlyExploring: apiResponseData.currentlyExploring,
          booksMentioned: apiResponseData.booksMentioned || [],
          versesReferenced: apiResponseData.versesReferenced || [],
        };

        const resolvedNodeId = findNodeIdByData(sourceNodeData);
        const nodeId = addHighlightedTextNode(
          responseData,
          resolvedNodeId
        );

        // Update biblical metrics if we have a topic
        if (apiResponseData.currentlyExploring) {
          updateMetrics({ ...responseData, nodeId }, resolvedNodeId);
        }

        // Summary is now handled silently in the backend as context for future API calls

        return nodeId;
      } catch (error) {
        console.error("Failed to handle text highlight:", error);
        // Check if it's a subscription error
        handleSubscriptionError(error);
        throw error;
      }
    },
    [
      askQuestion,
      addHighlightedTextNode,
      getSessionSummary,
      clearError,
      updateMetrics,
      getOptimizedData,
      findNodeIdByData,
      handleSubscriptionError,
    ]
  );

  // Handle custom question from Ask button
  const handleCustomQuestion = useCallback(
    async (question, sourceNodeData) => {
      try {
        clearError();

        // Create context from the source node
        const contextQuestion = `${question} (Context from: "${sourceNodeData.question}")`;

        // Build context array based on mode
        let contextArray;
        if (PERF_MODE === "V2") {
          // Optimized mode: use ancestor path only
          contextArray = getOptimizedData(sourceNodeData.nodeId);
        } else {
          // V1 mode: current behavior
          contextArray = [
            {
              question: sourceNodeData.question,
              response: sourceNodeData.response,
            },
          ];
        }

        const response = await askQuestion(contextQuestion, contextArray, false, user ? { personality: user.personality, name: user.name } : null);

        // The response structure is: { success: true, response: { response: "content", actions: [...], question: "..." } }
        const apiResponseData = response.response || {};

        const responseData = {
          response:
            apiResponseData.response ||
            "I'd be happy to help you learn about the Bible!",
          actions: apiResponseData.actions || [],
          question: question, // Use the user's question as the header
          timestamp: Date.now(),
          id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          sessionId: response.sessionId,
          interactionId: response.interactionId,
          isFromAskButton: true, // Flag to help with positioning
          isContinueJourney: response.isContinueJourney, // Pass through Continue Journey flag
          currentlyExploring: apiResponseData.currentlyExploring,
          booksMentioned: apiResponseData.booksMentioned || [],
          versesReferenced: apiResponseData.versesReferenced || [],
        };

        // Use addHighlightedTextNode which positions nodes relative to parent
        const resolvedNodeId = findNodeIdByData(sourceNodeData);
        const nodeId = addHighlightedTextNode(
          responseData,
          resolvedNodeId
        );

        // Update biblical metrics if we have a topic
        if (apiResponseData.currentlyExploring) {
          updateMetrics({ ...responseData, nodeId }, resolvedNodeId);
        }

        return nodeId;
      } catch (error) {
        console.error("Failed to handle custom question:", error);
        // Check if it's a subscription error
        handleSubscriptionError(error);
        throw error;
      }
    },
    [
      askQuestion,
      addHighlightedTextNode,
      clearError,
      updateMetrics,
      getOptimizedData,
      findNodeIdByData,
      handleSubscriptionError,
    ]
  );

  // Handle source click (books and verses)
  const handleSourceClick = useCallback(
    async (sourceType, sourceName, sourceNodeData) => {
      try {
        clearError();

        // Build context array based on mode
        let contextArray;
        if (PERF_MODE === "V2") {
          // Optimized mode: use ancestor path only
          contextArray = getOptimizedData(sourceNodeData.nodeId);
        } else {
          // V1 mode: current behavior
          contextArray = [
            {
              question: sourceNodeData.question,
              response: sourceNodeData.response,
              source: sourceName,
              sourceType: sourceType,
            },
          ];
        }

        let response;
        let displayQuestion;

        if (systemStyle === "2") {
          // V2: Use clean action codes, backend handles prompts
          let action;
          if (sourceType === "book") {
            action = "book_info";
            displayQuestion = `About the book of ${sourceName}`;
          } else if (sourceType === "verse") {
            action = "verse_info";
            displayQuestion = `Explain ${sourceName}`;
          } else {
            action = "ask";  // Fallback for unknown types
            displayQuestion = `About ${sourceName}`;
          }

          response = await queryAPI(action, sourceName, contextArray, false, user ? { personality: user.personality, name: user.name } : null);
        } else {
          // V1: Keep old behavior with frontend prompts
          let contextQuestion;

          if (sourceType === "book") {
            contextQuestion = `Tell me about the book of ${sourceName}. Provide an overview, key themes, historical context, and its significance in the Bible.`;
            displayQuestion = `About the book of ${sourceName}`;
          } else if (sourceType === "verse") {
            contextQuestion = `Please explain ${sourceName}. Provide the verse text, context, meaning, and significance.`;
            displayQuestion = `Explain ${sourceName}`;
          } else {
            contextQuestion = `Tell me more about ${sourceName}`;
            displayQuestion = `About ${sourceName}`;
          }

          response = await askQuestion(contextQuestion, contextArray);
        }

        // The response structure is: { success: true, response: { response: "content", actions: [...], question: "..." } }
        const apiResponseData = response.response || {};

        const responseData = {
          response:
            apiResponseData.response ||
            "I'd be happy to help you learn about the Bible!",
          actions: apiResponseData.actions || [],
          question: displayQuestion,
          timestamp: Date.now(),
          id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          sessionId: response.sessionId,
          interactionId: response.interactionId,
          isFromSource: true, // Flag for special positioning (bottom-left)
          sourceType: sourceType,
          sourceName: sourceName,
          currentlyExploring: apiResponseData.currentlyExploring,
          booksMentioned: apiResponseData.booksMentioned || [],
          versesReferenced: apiResponseData.versesReferenced || [],
        };

        // Use addHighlightedTextNode for source-triggered nodes
        const resolvedNodeId = findNodeIdByData(sourceNodeData);
        const nodeId = addHighlightedTextNode(
          responseData,
          resolvedNodeId
        );

        // Update biblical metrics if we have a topic
        if (apiResponseData.currentlyExploring) {
          updateMetrics({ ...responseData, nodeId }, resolvedNodeId);
        }

        return nodeId;
      } catch (error) {
        console.error("Failed to handle source click:", error);
        // Check if it's a subscription error
        handleSubscriptionError(error);
        throw error;
      }
    },
    [
      askQuestion,
      addHighlightedTextNode,
      clearError,
      updateMetrics,
      getOptimizedData,
      findNodeIdByData,
      handleSubscriptionError,
    ]
  );

  // Handle Reflect and Pray action
  const handleReflectAndPlayAction = useCallback(
    async (sourceNodeData) => {
      try {
        clearError();
        // console.log("🙏 Reflect & Pray button clicked!");

        // Build context array based on mode
        let contextArray;
        if (PERF_MODE === "V2") {
          // Optimized mode: use ancestor path only
          contextArray = getOptimizedData(sourceNodeData.nodeId);
        } else {
          // V1 mode: current behavior
          contextArray = [
            {
              question: sourceNodeData.question,
              response: sourceNodeData.response,
            },
          ];
        }

        let response;

        if (systemStyle === "2") {
          // V2 System: Backend handles the full prompt via PromptManager_v2.getReflectPrompt()
          // Pass minimal context - backend Reflect prompt knows to use conversation history
          response = await queryAPI("reflect", "reflect and pray", contextArray, false, user ? { personality: user.personality, name: user.name } : null);
        } else {
          // V1 System: Use old text-based detection with full prompt
          const contextQuestion = `Based on our conversation about "${sourceNodeData.question}", create a reflect and pray response.`;
          response = await askQuestion(contextQuestion, contextArray, false, user ? { personality: user.personality, name: user.name } : null);
        }

        const apiResponseData = response.response || {};

        // Debug: Log the response structure for reflection
        // console.log("🔍 handleReflectAndPlayAction - Full response:", response);
        // console.log(
        //   "🔍 handleReflectAndPlayAction - isReflection flag:",
        //   response.isReflection
        // );
        // console.log(
        //   "🔍 handleReflectAndPlayAction - apiResponseData:",
        //   apiResponseData
        // );

        const responseData = {
          response:
            apiResponseData.response || "Let's reflect and pray together!",
          actions: apiResponseData.actions || [],
          question: "Reflect and Pray",
          timestamp: Date.now(),
          id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          sessionId: response.sessionId,
          interactionId: response.interactionId,
          isReflectAndPray: true,
          isReflection: response.isReflection, // Pass through the isReflection flag from top level
          currentlyExploring: apiResponseData.currentlyExploring,
          booksMentioned: apiResponseData.booksMentioned || [],
          versesReferenced: apiResponseData.versesReferenced || [],
        };

        // console.log(
        //   "🔍 handleReflectAndPlayAction - Final responseData:",
        //   responseData
        // );

        const resolvedNodeId = findNodeIdByData(sourceNodeData);
        const nodeId = addHighlightedTextNode(
          responseData,
          resolvedNodeId
        );

        if (apiResponseData.currentlyExploring) {
          updateMetrics({ ...responseData, nodeId }, resolvedNodeId);
        }

        return nodeId;
      } catch (error) {
        console.error("Failed to handle Reflect and Pray action:", error);
        // Check if it's a subscription error
        handleSubscriptionError(error);
        throw error;
      }
    },
    [
      askQuestion,
      addHighlightedTextNode,
      clearError,
      updateMetrics,
      getOptimizedData,
      findNodeIdByData,
      handleSubscriptionError,
    ]
  );

  // Handle Explore action
  const handleExploreAction = useCallback(
    async (sourceNodeData) => {
      try {
        clearError();

        let response;

        if (systemStyle === "2") {
          // V2 System: Backend handles the full prompt via PromptManager_v2.getExplorePrompt()
          // No user input needed - backend knows to generate random Bible content
          // Build context array based on mode
          let contextArray = [];
          if (PERF_MODE === "V2" && sourceNodeData) {
            // Optimized mode: use ancestor path only
            contextArray = getOptimizedData(sourceNodeData.nodeId) || [];
          }

          response = await queryAPI(
            "explore",
            "random",  // Minimal trigger - backend Explore prompt handles random generation
            contextArray,
            false,
            user ? { personality: user.personality, name: user.name } : null
          );
        } else {
          // V1 System: Use original text manipulation approach with full frontend prompt
          // Generate timestamp seed for randomization
          const seedTimestamp = Date.now();

          // Diverse opening styles to prevent repetitive patterns
          const openingStyles = [
            "Start with a thought provoking questions",
            "Open with a life question",
            "Begin with Did you know?",
            "Start with a direct teaching about",
            "Begin with an inspiring question that introduces",
            "Open with a powerful Biblical story about",
            "Start by sharing God's wisdom on",
            "Begin with an encouraging word about",
            "Open with a practical life lesson on",
            "Start with a meaningful verse that teaches about",
            "Begin by exploring the Biblical concept of",
            "Open with a testimony or example about",
            "Start by explaining the significance of",
          ];

          // Select a random opening style based on timestamp
          const randomStyle =
            openingStyles[seedTimestamp % openingStyles.length];

          const contextQuestion = `Generate a completely random Bible topic that could help someone with their daily life challenges. This could be about:

Life areas: work, business, family, relationships, struggles, achievements, daily life, personal growth, decision-making, finances, health, friendship, leadership, forgiveness, hope, purpose, fear, stress, joy, peace, etc.

Bible content types: stories, verses, parables, teachings, characters, prophecies, miracles, wisdom literature, historical events, commandments, promises, etc.

Bible books: Any book from Genesis to Revelation

IMPORTANT INSTRUCTION: ${randomStyle} your chosen topic. Be creative and varied in your opening.

STRICTLY FORBIDDEN OPENING PHRASES - DO NOT USE:
- "navigating life's..."
- "finding with"
- "finding peace with"
- "discovering how"
- "let's explore"
- "in life's journey"
- "as we navigate"
- "walking through life"
- "life's challenges can"
- "when life gets"

Instead, use the selected opening style (${randomStyle}) to create a completely unique and engaging start. Each response should feel fresh, direct, and immediately valuable.

Create well-structured, educational content about your randomly selected topic. Write a COMPREHENSIVE response (minimum 1800 characters, aim for 1800 characters) that includes:
- A clear header/title
- Rich background context and story
- Multiple Bible verses or examples (at least 2-3)
- Detailed practical applications for daily life
- Additional insights or related teachings
- Use bullet points and short paragraphs for easy reading

IMPORTANT: This is an EXPLORE mode response, so provide SUBSTANTIAL CONTENT with multiple paragraphs, not just a title or brief summary. Include the actual Bible story, verses, context, and detailed practical applications. Make it comprehensive yet digestible. Use headers, bullets, and short sentences for mobile-friendly reading.

[Internal randomization: ${seedTimestamp}]`;

          response = await askQuestion(contextQuestion, []); // Empty context for true randomness
        }

        const apiResponseData = response.response || {};

        const responseData = {
          response:
            apiResponseData.response ||
            "Let's discover something random from the Bible!",
          actions: apiResponseData.actions || [],
          question: apiResponseData.isContinueJourney
            ? "Continue your spiritual journey"
            : "Discover",
          timestamp: Date.now(),
          id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
          sessionId: response.sessionId,
          interactionId: response.interactionId,
          isExplore: !apiResponseData.isContinueJourney,
          isContinueJourney: apiResponseData.isContinueJourney || false,
          currentlyExploring: apiResponseData.currentlyExploring,
          booksMentioned: apiResponseData.booksMentioned || [],
          versesReferenced: apiResponseData.versesReferenced || [],
        };

        // console.log("🔍 About to call addHighlightedTextNode with:", {
        //   responseData: responseData,
        //   sourceNodeId: sourceNodeData.nodeId,
        //   isContinueJourney: responseData.isContinueJourney,
        // });

        const resolvedNodeId = findNodeIdByData(sourceNodeData);
        const nodeId = addHighlightedTextNode(
          responseData,
          resolvedNodeId
        );

        // console.log("🔍 addHighlightedTextNode returned nodeId:", nodeId);

        if (apiResponseData.currentlyExploring) {
          updateMetrics({ ...responseData, nodeId }, resolvedNodeId);
        }

        return nodeId;
      } catch (error) {
        console.error("Failed to handle Explore action:", error);
        // Check if it's a subscription error
        handleSubscriptionError(error);
        throw error;
      }
    },
    [
      askQuestion,
      queryAPI,
      systemStyle,
      addHighlightedTextNode,
      clearError,
      updateMetrics,
      getOptimizedData,
      findNodeIdByData,
      handleSubscriptionError,
    ]
  );

  const value = {
    // Flow state
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onViewportChange,
    edgeType,
    setEdgeType,
    nodeCount,

    // API state
    isLoading,
    error,
    rateLimitError,
    subscriptionError,
    sessionId,
    interactionCount,

    // Interaction handlers
    handleQuestionSubmit,
    handleActionClick,
    handleTextHighlight,
    handleCustomQuestion,
    handleSourceClick,
    handleReflectAndPlayAction,
    handleExploreAction,

    // V2 API
    queryAPI,
    systemStyle,

    // Flow utilities
    focusNode,
    setReactFlowInstance,
    collapseAllNodes,
    expandAllNodes,
    getExpandedNodesCount,
    updateNodeExpansion,
    updateNodeData,
    bringNodeToFront,

    // Workflow management
    currentWorkflowName,
    setCurrentWorkflowName,
    currentFirestoreSessionId,
    setCurrentFirestoreSessionId,
    hasUnsavedChanges,
    isLoadingWorkflow,
    saveState,
    loadWorkflow,
    resetWorkflow,
    nodeIdCounter,
    clearAutosave,
    transferWorkflowOwnership,

    // Recovery management
    showRecoveryPrompt,
    recoveryData,
    recoverAutosave,
    dismissRecovery,
    lastAutosaveTime,

    // Biblical Exploration Metrics
    biblicalMetrics,

    // Exploration management
    startFreshExploration,

    // Utilities
    clearError,
    clearRateLimitError,
    clearSubscriptionError,
  };

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
};

// Custom hook to use Flow context
export const useFlowContext = () => {
  const context = useContext(FlowContext);
  if (!context) {
    throw new Error("useFlowContext must be used within a FlowProvider");
  }
  return context;
};

export default useFlowContext;
